/**
 * The creative partners we broker work to, in one table.
 *
 * Both the bundle dialog and `/contact` read from here. They used to hold their
 * own copies, which is exactly how a partner ends up named one way in a modal
 * and another way in the enquiry it opens.
 *
 * Note what is deliberately absent: no partner email, phone or website. Enquiries
 * are introduced by us, so their direct contact routes are not published on this
 * site at all — see the source note on their profile page for the one remaining
 * attribution, which is a citation rather than a way to reach them.
 */

export type Partner = {
  /** URL key, as it appears in `/contact?partner=…`. Lowercase. */
  key: string;
  name: string;
  /** Their wordmark, served from `public/partners/`. White ink — needs the navy plate. */
  logo: string;
  /** The brand-scope class that rebinds `--accent` to their palette. */
  plate: string;
  /** One line for the contact page's partner card. */
  blurb: string;
  /** What lands in the recipient's inbox, so it must read sensibly on its own. */
  subject: string;
  /** Their profile page on this site. */
  profile: string;
  /** What the bundle actually covers, in the partner's own terms. */
  bundle: string;
};

export const PARTNERS: Record<string, Partner> = {
  graphaize: {
    key: "graphaize",
    name: "Graphaize",
    logo: "/partners/graphaize-logo.png",
    plate: "partner-graphaize",
    blurb:
      "Explainer and educational video, infographics, pitch decks, ebooks, " +
      "microsites and social creative. Tell us what you need made and we will " +
      "put you in front of their strategist.",
    subject: "Creative content enquiry — Graphaize",
    profile: "/partners/graphaize",
    bundle:
      "their production work and your Simino Social plan, quoted together",
  },
};

/**
 * Resolve a `?partner=` value.
 *
 * A closed lookup on purpose: the value arrives from the URL, so anything not in
 * the table above is ignored rather than rendered. Nobody gets to put words on
 * our pages by crafting a link.
 */
export function findPartner(value: string | null | undefined): Partner | null {
  if (!value) return null;
  return PARTNERS[value.trim().toLowerCase()] ?? null;
}
